import type { ReactNode } from "react";
import { BarChart3, Building2, FileInput, QrCode, Search, Users } from "lucide-react";
import { DotChip } from "@/components/shared/DotChip";
import { makeTr, type LandingLocale } from "@/lib/landing/locale";
import { Container, MockFrame, Section, SectionHeading } from "./primitives";

/** A QR payment code: three finder squares and a fixed pseudo-random fill (decorative, encodes nothing). */
const QR_SIZE = 21;
const QR_CELLS = (() => {
  const cells: [number, number][] = [];
  const finder = (x: number, y: number) =>
    x < 7 && y < 7 && (x % 6 === 0 || y % 6 === 0 || (x > 1 && x < 5 && y > 1 && y < 5));
  let seed = 7;
  for (let y = 0; y < QR_SIZE; y++) {
    for (let x = 0; x < QR_SIZE; x++) {
      seed = (seed * 1103515245 + 12345) % 2147483648;
      const inFinder = (x < 8 && y < 8) || (x > 12 && y < 8) || (x < 8 && y > 12);
      const on = inFinder ? finder(x > 12 ? x - 14 : x, y > 12 ? y - 14 : y) : seed % 100 < 48;
      if (on) cells.push([x, y]);
    }
  }
  return cells;
})();
/** All cells as one path: a few hundred <rect>s would bloat the DOM. */
const QR_PATH = QR_CELLS.map(([x, y]) => `M${x} ${y}h1v1h-1z`).join("");

function QrMock() {
  return (
    <svg
      viewBox={`-1 -1 ${QR_SIZE + 2} ${QR_SIZE + 2}`}
      className="size-20 rounded-md bg-white"
      shapeRendering="crispEdges"
    >
      <path d={QR_PATH} fill="#111" />
    </svg>
  );
}

/** Everything beyond Fio matching: real, but secondary. Invoicing leads with a mockup. */
export function ExtrasSection({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);

  const tiles: { icon: ReactNode; title: string; text: string }[] = [
    {
      icon: <BarChart3 className="size-5" />,
      title: tr("Rozpočet a analytika", "Budget and analytics"),
      text: tr("Kolik vás firma stojí každý měsíc.", "What the company costs each month."),
    },
    {
      icon: <Users className="size-5" />,
      title: tr("Tým a účetní", "Team and accountant"),
      text: tr(
        "Členové a správci s vlastním přístupem.",
        "Members and admins with their own login."
      ),
    },
    {
      icon: <Building2 className="size-5" />,
      title: tr("Více firem", "Several companies"),
      text: tr("Pod jedním přihlášením.", "Under one login."),
    },
    {
      icon: <FileInput className="size-5" />,
      title: tr("Import faktur", "Invoice import"),
      text: tr(
        "Z Fakturoidu (XLSX) i z e-shopu Shoptet (ISDOC), spárované s platbami.",
        "From Fakturoid (XLSX) and from a Shoptet e-shop (ISDOC), matched with payments."
      ),
    },
  ];

  return (
    <Section id="dalsi-funkce" labelledBy="extras-title" tone="tinted">
      <Container>
        <SectionHeading
          id="extras-title"
          eyebrow={tr("Tarif Firma", "Firma plan")}
          title={tr("Navíc: fakturace, rozpočty a tým", "Also: invoicing, budgets and a team")}
        />
        <div className="mt-12 grid gap-4 lg:grid-cols-[1.25fr_1fr]">
          <div className="flex flex-col rounded-2xl border bg-card p-5 sm:p-6">
            <div className="flex items-center gap-3">
              <span className="flex size-10 items-center justify-center rounded-xl bg-primary/15 text-[hsl(var(--action))] dark:text-primary">
                <QrCode className="size-5" />
              </span>
              <div>
                <h3 className="text-base font-semibold tracking-tight">
                  {tr("Vystavování faktur", "Issuing invoices")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {tr(
                    "S QR platbou, šablonami a odběratelem z ARES.",
                    "With QR payment, templates and the customer from ARES."
                  )}
                </p>
              </div>
            </div>
            <MockFrame
              className="mt-5 flex-1 bg-background"
              label={tr(
                "Ukázka faktury s QR platbou a odběratelem načteným z ARES. Vymyšlená data.",
                "Sample invoice with a QR payment code and the customer loaded from ARES. Invented data."
              )}
            >
              <div className="grid gap-5 p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:p-5">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{tr("Faktura", "Invoice")}</p>
                  <p className="font-mono text-sm font-semibold">FA 2026-019</p>
                  <div className="mt-4 flex h-8 items-center gap-2 rounded-md border px-2.5 text-[13px]">
                    <Search className="size-3.5 text-muted-foreground" />
                    <span className="tabular-nums">IČO 12345678</span>
                    <DotChip color="green" className="ml-auto">
                      ARES
                    </DotChip>
                  </div>
                  <p className="mt-2 text-sm font-medium">Studio Lípa s.r.o.</p>
                  <p className="text-xs text-muted-foreground">Lipová 12, 602 00 Brno</p>
                  <div className="mt-4 divide-y rounded-md border text-[13px]">
                    <div className="flex justify-between gap-3 px-2.5 py-1.5">
                      <span className="break-words">{tr("Návrh webu", "Website design")}</span>
                      <span className="font-mono tabular-nums">36 000 Kč</span>
                    </div>
                    <div className="flex justify-between gap-3 px-2.5 py-1.5">
                      <span className="break-words">{tr("Fotografie", "Photography")}</span>
                      <span className="font-mono tabular-nums">12 400 Kč</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-end justify-between gap-4 sm:flex-col sm:items-end">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">{tr("K úhradě", "Amount due")}</p>
                    <p className="text-2xl font-semibold tabular-nums tracking-tight text-[hsl(var(--action))] dark:text-primary">
                      48 400 Kč
                    </p>
                  </div>
                  <QrMock />
                </div>
              </div>
            </MockFrame>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            {tiles.map((t) => (
              <li key={t.title} className="rounded-2xl border bg-card p-5">
                <span className="flex size-10 items-center justify-center rounded-xl bg-primary/15 text-[hsl(var(--action))] dark:text-primary">
                  {t.icon}
                </span>
                <h3 className="mt-4 text-base font-semibold tracking-tight">{t.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{t.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </Section>
  );
}
